import { t } from "i18next";
import moment from "moment";

interface IPerformaDateOptions {
  /**
   * Exibir dia da semana?
   */
  dayOfWeek?: boolean;

  /**
   * Modo de utilização das preposições
   */
  preposition?: "auto" | "none" | "always";

  /**
   * Exibir nomes longos?
   */
  longNames?: boolean;

  /**
   * Substituir os dias próximos por: Hoje, Amanhã, Há x dias
   */
  replaceClosestDays?: boolean;

  /**
   * Texto que será inserido antes da Data.
   */
  preText?: string;

  /**
   * Excluir horário?
   */
  excludeTime?: boolean;

  /**
   * Exibir preposição do horário?
   */
  showTimePreposition?: boolean;

  /**
   * Preposição utilizada antes do horário?
   */
  timePreposition?: string;
}

/**
 * Formatadores.
 */
export class Formaters {
  /**
   * Formata um conjunto de argumentos.
   * @param args
   * @returns
   */
  static format(...args: any[]): string {
    var i = 0;
    var string = args[i++];
    for (; i < args.length; i++) {
      string = string.replace(/\{\d+?\}/, args[i]);
    }
    return string;
  }

  /**
   * Trunca um texto em "n" caracteres de adiciona reticências ("...") ao final do texto.
   * @param str
   * @param maxLength
   * @returns
   */
  static truncateString(str: string, maxLength: number): string {
    if (str.length > maxLength) {
      return [str.slice(0, maxLength - 3), '...'].join('');
    }
    return str;
  }

  /**
   * Adiciona ZEROS à esquerda do número
   * @param n
   * @param digits
   * @returns
   */
  static padZero(n: number, digits: number): string {
    return ("0".repeat(digits) + n).slice(-digits);
  }


  /**
   * Formata um Período.
   * @param start
   * @param end
   * @returns
   */
  static dateRange(start: Date | null, end: Date | null): string | null {
    if (start == null && end == null) return null;

    if (start == null) return this.monthYear(end);
    else if (end == null) return this.monthYear(start);

    var lang = window.uiLanguage.toLowerCase();
    var s = moment(start).locale(lang);
    var e = moment(end).locale(lang);

    if (start.getFullYear() == end.getFullYear()) {
      if (start.getMonth() == end.getMonth())
        return s.format("DD") + " - " + e.format("DD/MMM");

      return s.format("DD/MMM") + " - " + e.format("DD/MMM");
    }
    else if (start.getFullYear() == new Date().getFullYear())
      return s.format("DD/MMM") + " - " + e.format("DD/MMM/YYYY");

    return s.format("DD/MMM/YYYY") + " - " + e.format("DD/MMM/YYYY");
  }

  /**
   * Formata um número contendo mês e dia (MMDD).
   * @param monthDay
   * @returns
   */
  static performaMonthDay(monthDay: number) {
    const m = monthDay / 100 - 1;
    const d = monthDay % 100;

    const currDate = new Date();
    var dt = new Date();
    if (currDate.getMonth() > m) dt.setFullYear(currDate.getFullYear() + 1);
    dt.setMonth(m);
    dt.setDate(d);

    return `${t("shared:Literal.Until")} ${moment(dt).format("DD/MMM")}`;
  }

  /**
   * Formata um Período de Tempo no padrão Performa.
   * @param start
   * @param end
   * @returns
   */
  static performaDateRange(start: Date | null, end: Date | null) {
    if (start == null && end == null) return null;

    if (start == null) return `${t("shared:Literal.Until")} ${this.momentFullDateTime(end!, false)}`;
    else if (end == null) return `${t("shared:Literal.FromDate")} ${this.momentFullDateTime(start, false)}`;

    return `${t("shared:Literal.Between")} ${this.momentFullDateTime(start, false)} ${t("shared:Literal.And")} ${this.momentFullDateTime(end!, false)}`;
  }

  /**
   * Formata uma data no padrão Performa.
   * @param date
   * @param options
   * @returns
   */
  static performaDate(
    date: Date | null,
    options?: IPerformaDateOptions
  ) {
    const { dayOfWeek = false, preposition = "auto", longNames = false, preText, excludeTime = false, showTimePreposition = true, timePreposition } = options ?? {};
    const uiLanguage = window.uiLanguage;

    if (date == null) return null;

    // pt-BR: DD/MMM(/YYYY) ÀS HH:MM
    // en-US: MMM/DD(/YYYY) AT HH:MM (AM|PM)
    // es-ES: DD/MMM(/YYYY) HH:MM
    const oneDayMiliseconds = 86400000;
    const now = new Date();
    const dateRef = new Date(date);
    const diffDays = (dateRef.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / oneDayMiliseconds;
    const maxSlashes = date.getFullYear() != now.getFullYear() ? 2 : 1;

    const showTime = !excludeTime && (!(date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0)
      && !(date.getHours() === 23 && date.getMinutes() === 59 && date.getSeconds() === 59));

    const day = diffDays >= -1 && diffDays <= 1 && (options == null || options.replaceClosestDays == true)
      ? this.capitalize(new Intl.RelativeTimeFormat(uiLanguage, { numeric: "auto" }).format(Math.trunc(diffDays), 'day')) // Ontem, Hoje, Amanhã
      : "";

    const prep = preposition == "always" || ((day == "" || preText) && preposition == "auto")
      ? new Intl.RelativeTimeFormat(uiLanguage).formatToParts(1, 'minute')[0].value // "em", "in"
      : "";

    let slashes = 0;

    const parts = new Intl.DateTimeFormat(uiLanguage, { dateStyle: 'full', timeStyle: 'long' }).formatToParts(date);

    return (options?.preText ? options?.preText + " " : "")
      + prep
      + day
      + parts.map((p) => {
        let r = "";
        if (p.type == "weekday" && dayOfWeek && day == "") r = this.capitalize(p.value) + ", ";

        if (day == "" && (p.type == "day" || p.type == "month" || p.type == "year")) {
          if (p.type == "month") r = !longNames ? this.capitalize(p.value.substring(0, 3)) : p.value.trim();
          if (p.type == "day") r = p.value.trim();
          if (p.type == "year" && maxSlashes == 2) r = p.value.trim();
          if (r != "" && slashes++ < maxSlashes) r += !longNames ? "/" : slashes == 1 ? parts[3].value : parts[5].value;
        }

        if (p.type == "hour" && showTime) {
          if (showTimePreposition) {
            r = " " + (timePreposition ?? parts[7].value.replace(",", "").trim()) + " " + p.value.trim() + ":";
          } else {
            r = " " + p.value.trim() + ":";
          }
        }
        if (p.type == "minute" && showTime) r = p.value.trim();
        if (p.type == "dayPeriod" && showTime) r = " " + p.value.trim();
        return r;
      }).join("");
  }

  /**
   * Formata a diferença de Tempo entre duas datas.
   * @param endDate
   * @param startDate
   * @returns Uma string no forma "nDdias Xhoras Yminutos"
   */
  static performaTimeDiff(endDate: Date, startDate: Date): string {
    var diff = endDate.getTime() - startDate.getTime();
    var d = Math.trunc(diff / 86400000);
    if (d > 0) diff -= (d * 86400000);
    var h = Math.trunc(diff / 3600000);
    if (h > 0) diff -= (h * 3600000);
    var m = Math.trunc(diff / 60000);

    if (h === 23 && m === 59) {
      d = d + 1;
      h = 0;
      m = 0;
    }

    var result = "";
    if (d > 0) result += `${d}d`;
    if (h > 0) result += `${d > 0 ? " " : ""}${h}h`;
    if (m > 0) result += `${h > 0 ? " " : ""}${Formaters.padZero(m, 2)}m`;

    return result;
  }

  /**
   * Formata uma Período, em formato longo, no padrão Performa, seguindo as opções desejadas.
   * @param startDate
   * @param endDate
   * @param options
   * @returns
   */
  static performaFullDateRange(startDate: Date, endDate: Date, options?: IPerformaDateOptions): string {
    const oneDay = 86400000; // 1 dia em milisgundos
    let diff = endDate.putEndOfDay().getTime() - startDate.putStartOfDay().getTime();
    let d = Math.trunc(diff / oneDay);
    if (d > 0) diff -= (d * oneDay);

    const { dayOfWeek = false, preposition = "auto", longNames = false, replaceClosestDays: replaceNextDays = true, excludeTime = false, showTimePreposition } = options ?? {};
    const usePreposition = (preposition == "auto" || preposition == "always");

    if (d > 0) {
      const separator = usePreposition ? t("shared:Literal.Until") : "-";
      const _longNames = longNames || (startDate.getFullYear() != endDate.getFullYear());

      if (excludeTime) return `${Formaters.momentFullDate(startDate, dayOfWeek, _longNames)} ${separator} ${Formaters.momentFullDate(endDate, dayOfWeek, _longNames)}`;
      else return `${Formaters.momentFullDateTime(startDate, dayOfWeek, _longNames)} ${separator} ${Formaters.momentFullDateTime(endDate, dayOfWeek, _longNames)}`;
    }
    else {

      const lang = window.uiLanguage.toLowerCase();
      const s = moment(new Date().setHours(0, 0, 0, 0)).locale(lang);
      const e = moment(startDate).locale(lang);
      let diffDays = e.diff(s, "days");
      if (diffDays == 0 && e.startOf('day') < s.startOf('day')) diffDays = -1;

      const hideStartTime = startDate.isInitialOrEndTime();
      const hideEndTime = endDate.isInitialOrEndTime();

      if (diffDays >= -1 && diffDays <= 1 && replaceNextDays) {

        const day = (new Intl.RelativeTimeFormat(window.uiLanguage, { numeric: "auto" }).format(diffDays, 'day')).toUpperCase();
        if (excludeTime) return day;

        let separator = "";
        if (hideStartTime && hideEndTime) separator = "";
        else if (!hideStartTime && !hideEndTime) separator = "-";
        else if (hideStartTime) separator = usePreposition ? t("shared:Literal.Until") : "";
        else separator = usePreposition ? t("shared:Literal.AtTime") : "";

        let time = "";
        if (!hideStartTime && !hideEndTime) time = separator + " " + moment(startDate).format("HH:mm") + " " + t("shared:Literal.AtTime") + " " + moment(endDate).format("HH:mm");
        else if (!hideStartTime) time = separator + " " + moment(startDate).format("HH:mm");
        else if (!hideEndTime) time = separator + " " + moment(endDate).format("HH:mm");

        return day + " " + time.trimStart();
      }
      else if (!hideEndTime) {
        return Formaters.performaDate(startDate, { dayOfWeek, longNames, preposition: "none", excludeTime, showTimePreposition, timePreposition: " - " })!
          + " "
          + t("shared:Literal.AtTime")
          + " "
          + moment(endDate).format("HH:mm");
      } else {
        return Formaters.performaDate(startDate, { dayOfWeek, longNames, preposition: "none", excludeTime, showTimePreposition })!;
      }
    }
  }

  /**
   * Formata uma Data, em formato longo, conforme os parâmetros especificados, utilizando a bibiloteca Moment.
   * @param date
   * @param includeDayOfWeek
   * @param longNames
   * @param separator
   * @returns
   */
  static momentFullDate(date: Date, includeDayOfWeek: boolean = false, longNames: boolean = false) {
    const displayDayOfWeek = includeDayOfWeek ? moment(date).format("dddd") : "";
    const displayDate = Formaters.dayMonth(date, { longNames });

    let str = "";
    if (includeDayOfWeek) str += displayDayOfWeek + ", ";
    str += displayDate;

    return str;
  }

  /**
   * Data e Hora formatada utilizando a bibiloteca Moment.
   * @param date
   * @param includeDayOfWeek
   * @param longNames
   * @returns
   */
  static momentFullDateTime(date: Date, includeDayOfWeek: boolean = false, longNames: boolean = false, separator: string = "") {
    const displayDayOfWeek = includeDayOfWeek ? this.capitalize(moment(date).format("dddd")) : "";
    const displayDate = Formaters.dayMonth(date, { longNames });
    var displayTime = "";

    if (!date.isInitialOrEndTime()) {
      if (separator) displayTime = " " + separator + " ";
      displayTime += moment(date).format("HH:mm");

    }

    if (includeDayOfWeek) return `${displayDayOfWeek}, ${displayDate} ${displayTime}`;

    return `${displayDate} ${displayTime}`;
  }

  /**
   * Tempo decorrido
   * @param seconds
   * @returns Uma string no formato "h m s"
   */
  static playTime(seconds?: number): string {
    if (typeof (seconds) != "number") return "?";
    var h = Math.floor(seconds / 3600);
    var m = Math.floor((seconds - (seconds / 3600)) / 60);
    var s = Math.floor((seconds - (seconds / 3600)) % 60);
    return Formaters.format("{0}h {1}m {2}s", h, m, s);
  }

  /**
   * Tempo decorrido
   * @param seconds
   * @returns Uma string no formato "hh:mm:ss"
   */
  static playTimer(seconds?: number): string {
    if (typeof (seconds) != "number") return "?";
    if (seconds <= 0) return "00:00:00";

    var h = Math.floor(seconds / 3600); seconds = seconds % 3600;
    var m = Math.floor(seconds / 60); seconds = seconds % 60;
    var s = Math.ceil(seconds);
    return Formaters.format("{0}:{1}:{2}", Formaters.padZero(h, 2), Formaters.padZero(m, 2), Formaters.padZero(s, 2));
  }

  /**
   * Converte um valor timespan em uma palavra no formato "XXhYYm", exemplo: "10h50m"
   * @param ms
   * @returns
   */
  static timespanToWord(ms: number): string {
    if (ms <= 0) return "0m";

    const totalSeconds = Math.floor(ms / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);

    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;

    const parts: string[] = [];

    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    // Shows minutes if they exist, or if the total duration is less than 1 hour
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

    return parts.join('');
  }

  /**
   * Tempo decorrido com formato ajustável.
   * @param ts
   * @returns
   */
  static durationToPlayTime(ts: number): string {
    var days = Math.floor(ts / (60 * 60 * 24));
    ts -= days * (60 * 60 * 24);

    var hours = Math.floor(ts / (60 * 60));
    ts -= hours * (60 * 60);

    var mins = Math.floor(ts / 60);
    ts -= mins * 60;

    var seconds = Math.floor(ts);

    return hours > 0
      ? Formaters.format("{0}:{1}:{2}h", hours, Formaters.padZero(mins, 2), Formaters.padZero(seconds, 2))
      : mins > 0
        ? Formaters.format("{0}:{1}m", Formaters.padZero(mins, 2), Formaters.padZero(seconds, 2))
        : Formaters.format("{0}s", Formaters.padZero(seconds, 2))
      ;
  }

  /**
   * Transforma um número contendo a quantidade de ticks, em uma string ajustável contendo "Dias, Horas, Minutos e Segundos".
   * @param ts
   * @returns
   */
  static ticksToDurationString(ts: number): string {
    ts = ts / 10000;

    var days = Math.floor(ts / (1000 * 60 * 60 * 24));
    ts -= days * (1000 * 60 * 60 * 24);

    var hours = Math.floor(ts / (1000 * 60 * 60));
    ts -= hours * (1000 * 60 * 60);

    var mins = Math.floor(ts / (1000 * 60));
    ts -= mins * (1000 * 60);

    var seconds = Math.floor(ts / (1000));

    if (days > 0)
      return Formaters.format("{0}, {1}:{2}:{3}s", days, hours, Formaters.padZero(mins, 2), Formaters.padZero(seconds, 2));
    else if (hours > 0)
      return Formaters.format("{0}:{1}:{2}s", hours, Formaters.padZero(mins, 2), Formaters.padZero(seconds, 2));
    else
      return Formaters.format("{0}:{1}s", mins, Formaters.padZero(seconds, 2));
  }

  /**
   * Transforma um número contendo a quantidade de ticks no formato playtime.
   * @param ts
   * @returns
   */
  static ticksToPlayTime(ts: number): string {
    ts = ts / 10000000;

    var days = Math.floor(ts / (60 * 60 * 24));
    ts -= days * (60 * 60 * 24);

    var hours = Math.floor(ts / (60 * 60));
    ts -= hours * (60 * 60);

    var mins = Math.floor(ts / 60);
    ts -= mins * 60;

    var seconds = Math.floor(ts);

    return days > 0
      ? Formaters.format("{0}d {0}:{1}h", hours, Formaters.padZero(mins, 2))
      : hours > 0
        ? Formaters.format("{0}:{1}h", hours, Formaters.padZero(mins, 2))
        : mins > 0
          ? Formaters.format("{0}m", mins)
          : Formaters.format("{0}s", seconds)
      ;
  }

  /**
   * Formata uma string contendo um Timespan.
   * @param ts
   * @returns
   */
  static timespan(ts?: string): string | null {
    if (!ts) return null;
    var d = ts.split('.');
    return (d.length == 1 ? d[0] : d[0] + ":" + d[1].substring(0, 2));
  }

  /**
   * Formata uma string contendo um Timespan no formato "Zd Xh Ym".
   * @param hour
   * @returns
   */
  static timeSpanToTime(hour: string): string {
    var d = hour.split(':');
    var h = parseInt(d[0]);
    var m = parseInt(d[1]);
    var s = parseInt(d[2]);

    if (h > 0 && m == 0 && s == 0) {
      return Formaters.format("{0}h", h);
    }
    else if (h == 0 && m > 0 && s == 0) {
      return Formaters.format("{0}m", m);
    }
    else if (h == 0 && m == 0 && s >= 0) {
      return Formaters.format("{0}s", s);
    }
    else if (h == 0 && m > 0 && s > 0) {
      return Formaters.format("{0}m{1}s", m, s);
    }

    if (s == 0)
      return Formaters.format("{0}h{1}m", h, m);

    return Formaters.format("{0}h{1}m{2}s", h, m, s);
  }

  /**
   * Converte segundos em timespan no formato "HH:MM:SS".
   * @param seconds
   * @returns
   */
  static secondsToTimeSpan(seconds?: number): string {
    if (typeof (seconds) != "number") return "00:00:00";
    if (seconds <= 0) return "00:00:00";

    var h = Math.floor(seconds / 3600);
    var m = Math.floor((seconds % 3600) / 60);
    var s = Math.floor(seconds % 60);

    return Formaters.format("{0}:{1}:{2}", Formaters.padZero(h, 2), Formaters.padZero(m, 2), Formaters.padZero(s, 2));
  }

  /**
   * Transforma Segundos em Horas no formato "Zd Xh Ym"
   * @param seconds
   * @returns
   */
  static secondsToFullTime(seconds?: number): string {
    if (typeof (seconds) != "number") return "?";
    if (seconds <= 0) return "00:00:00";

    if (seconds < 60)
      return `${seconds} ${seconds > 1 ? t("shared:Literal.Seconds") : t("shared:Literal.Second")}`;

    const days = Math.floor(seconds / (24 * 3600));
    seconds %= (24 * 3600);
    let hours = Math.floor(seconds / 3600);
    seconds %= 3600;
    let minutes = Math.floor(seconds / 60);
    seconds %= 60;

    if (days == 0 && hours == 0 && minutes > 0) {
      if (seconds >= 30) minutes++;
      return `${minutes} ${minutes > 1 ? t("shared:Literal.Minutes") : t("shared:Literal.Minute")}`;
    }
    if (days == 0 && hours > 0 && minutes == 0) {
      return `${hours} ${hours > 1 ? t("shared:Literal.Hours") : t("shared:Literal.Hour")}`;
    }
    if (days == 0 && hours > 0 && minutes > 0) {
      if (seconds >= 30) minutes++;
      return `${hours}h${Formaters.padZero(minutes, 2)}m`;
    }
    if (days > 0) {
      return `${days}d${Formaters.padZero(hours, 2)}h${Formaters.padZero(minutes, 2)}m`;
    }

    return `${hours}h${Formaters.padZero(minutes, 2)}m`;
  }

  /**
   * Transforma o tempo de Duração (em segundos) no formato "Z h" ou "Zhh:Xmm h"
   * @param duration
   * @returns
   */
  static duration(duration: number): string {
    const h = duration / 100;
    const m = duration % 100;
    return m == 0 ? `${h} h` : `${h}:${this.padZero(m, 2)} h`;
  }

  /**
   * Data em formato relativo.
   * Exemplo:
   * - há poucos segundos
   * - 40 segundos atrás
   * - há um minuto
   * - há 2 minutos
   * - há 1 hora
   * - há 1 mês
   * - há 1 ano
   * @param dt
   * @returns
   */
  static relativeTime(dt?: Date | null): string | null {
    if (dt == null) return null;

    var lang = window.uiLanguage.toLowerCase();
    var m = moment(dt).locale(lang);
    return m.fromNow();
  }

  /**
   * Data em formato Calendário.
   * Exemplo:
   * - Última Segunda às 14:00h
   * - Ontém/Hoje/Amanhã as 14:00h
   * - Outros casos, a data no formato dd/mm/yyyy
   * @param dt
   * @returns
   */
  static relativeCalendar(dt?: Date | null): string | null {
    if (dt == null) return null;

    var lang = window.uiLanguage.toLowerCase();
    var m = moment(dt).locale(lang);
    return m.calendar();
  }

  /**
   * Número formato em "n"" casas decimais.
   * @param n
   * @param decimals
   * @returns
   */
  static number(n: number, decimals: number): string {
    return n.toLocaleString(window.uiLanguage, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true
    });
  }

  /**
       * Número formato com o sinal de '+' caso o valor seja maior ou igual a zero; '-' se negativo
       * @param n
       * @param decimals
       * @returns
       */
  static numberSignal(n: number, options?: Intl.NumberFormatOptions): string {
    return (n >= 0 ? "+" : "") + n.toLocaleString(window.uiLanguage, options ?? { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true });
  }


  /**
   * Número Percentual.
   * Se parte fracionária é descartada se for ZERO.
   * @param n
   * @param decimals
   * @returns
   */
  static percent(n: number, decimals: number): string {
    const whole = Math.trunc(n);
    const fractional = n - whole;
    if (fractional > 0) return n.toFixed(decimals);
    return n.toFixed(0);
  }

  /**
   * Número Percentual em formato string, incluindo '%'.
   * Se parte fracionária é descartada se for ZERO.
   * @param n
   * @param decimals
   * @returns
   */
  static percentString(n: number, decimals: number): string {
    return this.percent(n, decimals) + "%";
  }

  /**
 * Formata um número percentual
 * Remove as casas decimais se a parte fracionária for zero.
 * @param n
 * @param decimals
 * @param includeSymbol
 */
  static percentLocale(n: number, decimals: number, includeSymbol: boolean = true): string {
    const hasFraction = n % 1 !== 0;

    const formattedNumber = n.toLocaleString(window.uiLanguage, {
      minimumFractionDigits: hasFraction ? decimals : 0,
      maximumFractionDigits: hasFraction ? decimals : 0,
      useGrouping: true
    });

    return includeSymbol ? `${formattedNumber}%` : formattedNumber;
  }

  /**
   * Número em formatação do Tipo Moeda.
   * @param n
   * @returns
   */
  static currency(n: number, simbol?: string): string {
    return (simbol ? (simbol + " ") : "") + Formaters.number(n, 2);
  }

  /**
   * Número formatado conforme Localização Atual do navegador.
   * @param v
   * @param options
   * @returns
   */
  static localeNumber(v: number | null, options?: Intl.NumberFormatOptions): string | null {
    if (v == null) return null;

    var lang = window.uiLanguage.toLowerCase();
    return v.toLocaleString(lang, options);
  }

  /**
   * Data formatada conforme Localização Atual do navegador.
   * @param dt
   * @param nullValue
   * @returns
   */
  static localeDate(dt?: Date | null, nullValue: string | null = null): string | null {
    if (dt == null) return nullValue;

    var lang = window.uiLanguage.toLowerCase();
    var m = moment(dt).locale(lang);
    return m.format("L");
  }

  /**
   * Horário formatado conforme Localização Atual do navegador.
   * @param dt
   * @param nullValue
   * @returns
   */
  static localeTime(dt?: Date | null, nullValue: string | null = null): string | null {
    if (dt == null) return nullValue;

    var lang = window.uiLanguage.toLowerCase();
    var m = moment(dt).locale(lang);
    return m.format("LT");
  }

  /**
   * Data e Hora formatada conforme Localização Atual do navegador.
   * @param dt
   * @param separator
   * @param nullValue
   * @param hideTimeZero
   * @returns
   */
  static localeDatetime(dt?: Date | null, separator: string = " ", nullValue: string | null = null, hideTimeZero: boolean | null = null): string | null {
    const timeText = this.localeTime(dt);
    if (hideTimeZero && timeText == "00:00")
      return dt == null ? nullValue : this.localeDate(dt);
    else
      return dt == null ? nullValue : this.localeDate(dt) + separator + this.localeTime(dt);
  }

  static momentShortDatetime(dt?: Date | null): string | null {
    if (dt == null) return null;

    var mdt = moment(dt);

    return mdt.diff(moment(), "year") == 0
      ? mdt.format("DD/MMM hh:mm")
      : mdt.format("DD/MMM/YYYY HH:mm");
  }

  /**
   * Data no formato "dias/mês".
   * @param dt
   * @param options
   * @returns
   */
  static dayMonth(dt?: Date | null, options?: IPerformaDateOptions): string | null {
    if (dt == null) return null;

    var lang = window.uiLanguage.toLowerCase();
    var m = moment(dt).locale(lang);
    var isCurrentYear = m.year() === moment().year();

    const { longNames = false, dayOfWeek = false } = options ?? {};

    if (longNames) {
      var regexRemoveYear = /,?\s*(de\s+|of\s+)?\d{4}|\d{4}(,?\s*)?(de|of)?\s*/g;

      var displayDate = dayOfWeek ? m.format("dddd, LL") : m.format("LL");
      return this.capitalize(
        isCurrentYear
          ? displayDate.replace(regexRemoveYear, '')
          : displayDate
      );
    } else {
      return isCurrentYear
        ? m.format("DD/MMM")
        : m.format("L");
    }
  }

  /**
   * Data no formato "mês/ano".
   * @param dt
   * @param hideCurrentYear
   * @returns
   */
  static monthYear(dt: Date | null, hideCurrentYear: boolean = false): string | null {
    if (dt == null) return null;

    var lang = window.uiLanguage.toLowerCase();
    var m = moment(dt).locale(lang);

    return dt.getFullYear() == new Date().getFullYear() && hideCurrentYear
      ? m.format("MMM")
      : m.format("MMM/YYYY");
  }

  /**
   * Diferença entre datas em número de Dias.
   * @param end
   * @param start
   * @returns
   */
  static dateDiffDays(end: Date, start?: Date | null): number | null {
    if (start == null && end == null) return null;

    var lang = window.uiLanguage.toLowerCase();
    var s = moment(start).locale(lang);
    var e = moment(end).locale(lang);

    var diffDays = e.diff(s, "days");
    return diffDays;
  }

  /**
   * Diferença entre Datas no formato relativo (somente dias, hoje, amanhã, x dias ou data completa).
   * @param end
   * @param start
   * @param options
   * @returns
   */
  static dateDiffRelative(end: Date, start?: Date | null, options?: IPerformaDateOptions): string | null {
    if (start == null && end == null) return null;

    const { preposition = "auto" } = options ?? {};

    var lang = window.uiLanguage.toLowerCase();
    var s = moment(start).locale(lang);
    var e = moment(end).locale(lang);

    var diffDays = e.diff(s, "days");
    if (diffDays == 0) return t("shared:Literal.Today");
    else if (diffDays == 1) return t("shared:Literal.Tomorrow");
    else if (diffDays < 30) return ((preposition == "auto" || preposition == "always") ? `${t("shared:Literal.In")} ` : "") + diffDays + ` ${t("shared:Literal.Days")}`;
    else if (diffDays < 366) return ((preposition == "auto" || preposition == "always") ? ` ${t("shared:Literal.Until")} ` : "") + e.format("DD/MMM");
    return ` ${t("shared:Literal.Until")} ` + e.format("DD/MMM/YYYY");
  }

  /**
   * Diferença de tempo entre duas datas no formato "hh:mm:ss".
   * @param endDate
   * @param startDate
   * @returns
   */
  static timeDiff(endDate: Date, startDate: Date): string {
    var diff = endDate.getTime() - startDate.getTime();
    var h = Math.round(diff / 86400000);
    var m = Math.round(((diff % 86400000) / 3600000));
    var s = Math.round(((diff % 86400000) % 3600000) / 60000);
    return h + ":" + Formaters.padZero(m, 2) + ":" + Formaters.padZero(s, 2);
  }

  /**
   * Adiciona dias a uma data.
   * @param date
   * @param days
   * @returns
   */
  static addDays(date: Date, days: number) {
    var result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Transforma o tamanho do arquivo em bytes para Gb, Mb ou Kb.
   * @param fileSize
   * @returns
   */
  static fileSize(fileSize?: number): string {
    if (fileSize == null)
      return "";

    const fs = Math.abs(fileSize);
    if (fs >= 1073741824)
      return Formaters.number(fileSize / 1024.0 / 1024.0 / 1024.0, 0) + " GB";
    if (fs >= 1048576)
      return Formaters.number(fileSize / 1024.0 / 1024.0, 0) + " MB";
    if (fs >= 1024)
      return Formaters.number(fileSize / 1024.0, 0) + " KB";

    return Formaters.number(fileSize, 0) + " Bytes";
  }

  /**
   * Número em formato reduzido.
   * @param fileSize
   * @returns  Se maior que um milhão, 1M; se maior que mil, 1K; senão o próprio valor;
   */
  static numberShortView(value?: number): string {
    if (value == null)
      return "";

    if (value >= 1000000) {
      const decimals = value % 1000000 == 0 ? 0 : 1;
      return Formaters.number(value / 1000000.0, decimals) + "M";
    }
    if (value >= 1000) {
      const decimals = value % 1000 == 0 ? 0 : 1;
      return Formaters.number(value / 1000.0, decimals) + "K";
    }
    return value.toString();
  }

  /**
   * Tornar uma string acentuada ou não acentuada, de acordo com as letras acentuadas presentes na string.
   * @param words
   * @returns
   */
  static getAccentInsensitiveWords(words: string) {
    var accentedForms: Record<string, string> = {
      'c': 'ç',
      'C': 'Ç',
      'a': 'áàãä',
      'A': 'ÁÀÃÄ',
      'e': 'éè',
      'E': 'ÉÈ',
      'i': 'íìï',
      'I': 'ÍÌÏ',
      'o': 'óòõö',
      'O': 'ÓÒÕÖ',
      'u': 'úùü',
      'U': 'ÚÙÜ',
    };

    for (var s in accentedForms) {
      words = words.replace(new RegExp(s, 'g'), '[' + s + accentedForms[s] + ']');
    }

    return words;
  }

  /**
   * Destaca os termos de uma string.
   * @param term
   * @param searchText
   * @param caseSensitive
   * @param accentSensitive
   * @returns Uma string em formato HTML.
   */
  static htmlHighlightTerm(term: string, searchText: string, caseSensitive: boolean = false, accentSensitive: boolean = false): string {
    const wrapper = 'b';
    const regexFlags = caseSensitive ? 'g' : "gi";

    if (term == null || term == "" || searchText == null || searchText == "") return searchText;

    function wrap(match: string): string {
      return '<' + wrapper + '>' + match + '</' + wrapper + '>';
    }

    const nterm = accentSensitive ? term : this.getAccentInsensitiveWords(term);
    const regex = new RegExp(nterm, regexFlags);
    return searchText.replace(regex, match => wrap(match));
  }

  /**
   * Coloca a Primeira Letra da string em maiúscula.
   * @param text
   * @returns
   */
  static capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /**
   * Coloca todas as palavras que estão na string com a Primeira Letra em maiúscla.
   * @param text
   * @returns
   */
  static capitalizeAll(text: string) {
    return text.replace(/(?:^|\s)\S/g, function (a) { return a.toUpperCase(); });
  }

  /**
   * Transforma o texto em um Título com as Primeiras Letras de cada palavra em maíuscula, exceto preposições.
   * @param text
   * @returns
   */
  static capitalizedTitle(text: string) {
    var words = text.match(/[a-zA-Z0-9áéíóúÁÉÍÓÚàèìòùÀÈÌÒÙäëïöüÄËÏÖÜãõÃÕâêîôûÂÊÎÔÛçÇ]+/g),
      preps = [
        "of", "on", "and", "or", "in", "a", "the",
        "da", "de", "do", "em", "na", "no", "a", "e", "i", "o", "u", "ou"
      ];
    return words?.map((e, i) => preps.indexOf(e) == -1 || i === 0 ? e[0].toUpperCase() + e.slice(1) : e).join(" ");
  }

  /**
   * Retorna o ano e mês atual no formato "yyyymm".
   * @returns
   */
  static getCurrentYearMonth() {
    const dt = new Date();
    return this.getYearMonth(dt);
  }

  /**
   * Retorna o ano e mês no formato "yyyymm".
   * @param dt
   * @returns
   */
  static getYearMonth(dt: Date) {
    return (dt.getUTCFullYear() * 100) + dt.getUTCMonth() + 1;
  }

  /**
   * Retorna uma data a partir de um número no formato "yyyymm".
   * @param yearMonth
   * @returns
   */
  static getDateFromYearMonth(yearMonth: number) {
    var day = moment(yearMonth, "YYYYMM");
    return day.toDate();
  }

  /**
   * Formata um XML de forma legível, com indentação e quebras de linha.
   * @param sourceXml
   * @returns
   */
  static prettyXml(sourceXml: string) {

    var xmlDoc = new DOMParser().parseFromString(sourceXml, 'application/xml');
    var xsltDoc = new DOMParser().parseFromString([
      // describes how we want to modify the XML - indent everything
      '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">',
      '  <xsl:strip-space elements="*"/>',
      '  <xsl:template match="para[content-style][not(text())]">', // change to just text() to strip space in text nodes
      '    <xsl:value-of select="normalize-space(.)"/>',
      '  </xsl:template>',
      '  <xsl:template match="node()|@*">',
      '    <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>',
      '  </xsl:template>',
      '  <xsl:output indent="yes"/>',
      '</xsl:stylesheet>',
    ].join('\n'), 'application/xml');

    var xsltProcessor = new XSLTProcessor();
    xsltProcessor.importStylesheet(xsltDoc);
    var resultDoc = xsltProcessor.transformToDocument(xmlDoc);
    var resultXml = new XMLSerializer().serializeToString(resultDoc);
    return resultXml;
  }

}
